import * as React from 'react';
import * as R from 'ramda';
import { Form, Text, Select, FormValues, FormErrors } from 'react-form';
import { BaseFormState, BaseFormProps } from '../../interfaces';
import {Organization, UserTypeahead, UserTeam, SurveyCompleteUser, User} from '../../models';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import {
    TypeaheadService,
    ValidationService,
    NotificationService,
    SurveyInstanceDataService,
    ReportDataService,
    UserDataService, Auth,
} from '../../services';
import { InputGroup } from 'react-bootstrap';
import { UserStore, FilterStore } from '../../stores';
import { observer, inject } from 'mobx-react';
import { computed } from 'mobx';

import './TeamForm.css';
import {LoadingIndicator} from "../loadingIndicator/LoadingIndicator";
import * as moment from "moment";
import Datepicker from "react-datepicker";
import {Prompt} from 'react-router';
import {BatchFunctions} from "../../constants/Permissions";

interface FormState extends BaseFormState {
    selectedOrganizationId?: number;
    selectedTeamMembers?: UserTypeahead[];
    selectedAssociateTeamMembers?: UserTypeahead[];
    selectedUserIdsForBatch: number[];
    selectedUserIdsForTypesReport: number[];
    selectedUserIdsForLimitedReport: number[];
    inviteSentByUserId: {};
    surveyCompletedByUserId: {};
    inviteExpiration: moment.Moment;
    confirmedTeamMembers: UserTeam[];
    confirmedAssociateTeamMembers: SurveyCompleteUser[];
    teamMemberTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    teamAssociateMemberTypeahead: {
        allowNew: boolean;
        isLoading: boolean;
        multiple: boolean;
        options: any[];
    };
    canSubmit: boolean;
    formErrors: FormErrors;
    isLoading: boolean;
    saveNeeded: boolean;
    showModal: boolean;
}

interface TeamFormProps extends BaseFormProps {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    organizations: Organization[];
    notificationService: NotificationService;
}

@inject('UserStore', 'FilterStore') @observer
export class TeamForm extends React.Component<TeamFormProps, FormState> {
    private surveyInstanceDataService = new SurveyInstanceDataService(this.props.notificationService);
    private userDataService = new UserDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);
    private vs = new ValidationService();
    private typeaheadService = new TypeaheadService(this.props.notificationService);
    private teamMemberTypeahead: any;
    private teamAssociateMemberTypeahead: any;

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get canBatch() {
        return this.userPermissions.includes(BatchFunctions);
    }

    constructor(props: TeamFormProps) {
        super(props);

        this.state = {
            confirmedTeamMembers: [],
            confirmedAssociateTeamMembers: [],
            selectedUserIdsForBatch: [],
            selectedUserIdsForTypesReport: [],
            selectedUserIdsForLimitedReport: [],
            inviteSentByUserId: {},
            surveyCompletedByUserId: {},
            inviteExpiration: moment().add(7, 'd'),
            teamMemberTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: true,
                options: [],
            },
            teamAssociateMemberTypeahead: {
                allowNew: false,
                isLoading: false,
                multiple: true,
                options: [],
            },
            canSubmit: false,
            formErrors: {},
            isLoading: false,
            saveNeeded: false,
            showModal: false,
        };

        this.handleSearch = this.handleSearch.bind(this);
        this.handleAssociateSearch = this.handleAssociateSearch.bind(this);
        this.formDidUpdate = this.formDidUpdate.bind(this);
        this.addUser = this.addUser.bind(this);
        this.removeUserBinder = this.removeUserBinder.bind(this);
        this.addAssociateUser = this.addAssociateUser.bind(this);
        this.removeAssociateUserBinder = this.removeAssociateUserBinder.bind(this);
        this.typeaheadOnChange = this.typeaheadOnChange.bind(this);
        this.typeaheadAssociateOnChange = this.typeaheadAssociateOnChange.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.handleIsManagerChangeBinder = this.handleIsManagerChangeBinder.bind(this);
        this.handleIsMemberChangeBinder = this.handleIsMemberChangeBinder.bind(this);

        this.validateErrors = this.validateErrors.bind(this);
        this.validateSuccess = this.validateSuccess.bind(this);
    }

    private handleSearch(filter: string) {
        const { selectedOrganizationId, teamMemberTypeahead, confirmedTeamMembers } = this.state;
        if (selectedOrganizationId) {
            this.setState({ teamMemberTypeahead: Object.assign({}, teamMemberTypeahead, { isLoading: true }) });

            this.typeaheadService.getUserTypeaheadsByOrganization(selectedOrganizationId, filter)
                .then((response: UserTypeahead[]) => {
                    const potentialMembers = response.filter((user) => !confirmedTeamMembers.some(x => x.userID === user.id));
                    this.setState({
                        teamMemberTypeahead: Object.assign(
                            {},
                            teamMemberTypeahead,
                            {
                                isLoading: false,
                                options: potentialMembers
                            }
                        )
                    });
                });
        }
    }

    private handleAssociateSearch(filter: string) {
        const { selectedOrganizationId, teamAssociateMemberTypeahead, confirmedAssociateTeamMembers } = this.state;
        if (selectedOrganizationId) {
            this.setState({ teamAssociateMemberTypeahead: Object.assign({}, teamAssociateMemberTypeahead, { isLoading: true }) });

            this.typeaheadService.getUserTypeaheadsByRole(selectedOrganizationId, filter)
                .then((response: UserTypeahead[]) => {
                    const potentialMembers = response.filter((user) => !confirmedAssociateTeamMembers.some(x => x.userID === user.id));
                    this.setState({
                        teamAssociateMemberTypeahead: Object.assign(
                            {},
                            teamAssociateMemberTypeahead,
                            {
                                isLoading: false,
                                options: potentialMembers
                            }
                        )
                    });
                });
        }
    }

    componentDidMount() {
        if (this.props.defaultValues) {
            this.setState({
                selectedOrganizationId: this.props.defaultValues.organizationId,
                confirmedTeamMembers: this.props.defaultValues.members,
                confirmedAssociateTeamMembers: this.props.defaultValues.surveyCompleteUsers
            });
        }
    }

    private formDidUpdate(formState: any) {
        if (formState.values) {
            if (formState.values.organizationId) {
                this.setState({ selectedOrganizationId: formState.values.organizationId });
            }
        }
    }

    private typeaheadOnChange(selected: Array<Object | String>) {
        this.setState({ selectedTeamMembers: selected as UserTypeahead[] });
    }

    private typeaheadAssociateOnChange(selected: Array<Object | String>) {
        this.setState({ selectedAssociateTeamMembers: selected as UserTypeahead[] });
    }

    componentDidUpdate() {
        for (const userteam of this.state.confirmedTeamMembers) {
            if (userteam.userID && this.state.inviteSentByUserId[userteam.userID] === undefined) {
                this.state.inviteSentByUserId[userteam.userID] = null;
                this.userHasInvite(userteam.userID!).then((value) => {
                    this.state.inviteSentByUserId[userteam.userID!] = value;
                    this.forceUpdate();
                });
            }
            if (userteam.userID && this.state.surveyCompletedByUserId[userteam.userID] === undefined) {
                this.state.surveyCompletedByUserId[userteam.userID] = null;
                this.getLatestCompletedInstanceForUserId(userteam.userID).then((instance) => {
                    if (instance === null) {
                        this.state.surveyCompletedByUserId[userteam.userID!] = false;
                    } else {
                        this.state.surveyCompletedByUserId[userteam.userID!] = instance;
                    }
                    this.forceUpdate();
                });
            }
        }
    }

    private addUser(e: any) {
        e.preventDefault();
        const { selectedTeamMembers, confirmedTeamMembers } = this.state;

        if (selectedTeamMembers && selectedTeamMembers.length > 0) {
            const newMembers = selectedTeamMembers.filter(user => {
                return !confirmedTeamMembers.some(x => x.userID === user.id);
            });

            const userTeams: UserTeam[] = newMembers.map(user => {
                return {
                    user: user,
                    userID: user.id,
                    isManager: false,
                    isMember: true
                };
            });

            this.setState({
                confirmedTeamMembers: [...confirmedTeamMembers, ...userTeams],
                selectedTeamMembers: []
            });

            this.teamMemberTypeahead.getInstance().clear();
        }
    }

    private removeUser(userId: number) {
        const { confirmedTeamMembers } = this.state;

        this.setState({ confirmedTeamMembers: confirmedTeamMembers.filter((x: UserTeam) => x.userID !== userId) });
    }

    private removeUserBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {

        return this.removeUser.bind(this, id);
    }

    private addAssociateUser(e: any) {
        e.preventDefault();
        const { selectedAssociateTeamMembers, confirmedAssociateTeamMembers } = this.state;

        if (selectedAssociateTeamMembers && selectedAssociateTeamMembers.length > 0) {
            const newMembers = selectedAssociateTeamMembers.filter(user => {
                return !confirmedAssociateTeamMembers.some(x => x.userID === user.id);
            });

            const userTeams: SurveyCompleteUser[] = newMembers.map(user => {
                var u = new User();
                u.id = user.id;
                u.firstName = user.firstName;
                u.lastName = user.lastName;
                return {
                    id: 0,
                    userID: user.id,
                    user: u
                };
            });

            this.setState({
                confirmedAssociateTeamMembers: [...confirmedAssociateTeamMembers, ...userTeams],
                selectedAssociateTeamMembers: []
            });

            this.teamAssociateMemberTypeahead.getInstance().clear();
        }
    }

    private removeAssociateUser(userId: number) {
        const { confirmedAssociateTeamMembers } = this.state;

        this.setState({ confirmedAssociateTeamMembers: confirmedAssociateTeamMembers.filter((x: SurveyCompleteUser) => x.userID !== userId) });
    }

    private removeAssociateUserBinder(id: number): ((event: React.MouseEvent<HTMLElement>) => void) {

        return this.removeAssociateUser.bind(this, id);
    }

    private handleIsManagerChange(userId: number) {
        const { confirmedTeamMembers } = this.state;

        const teamMembers = confirmedTeamMembers.map((x: UserTeam) => {
            if (x.userID !== userId) {
                return x;
            }

            x.isManager = !x.isManager;
            return x;
        });

        this.setState({ confirmedTeamMembers: teamMembers, saveNeeded: true });
    }

    private handleIsManagerChangeBinder(id: number): any {
        return this.handleIsManagerChange.bind(this, id);
    }

    private async handleSelectUser(id: number) {
        let batch = this.state.selectedUserIdsForBatch;
        const index = batch.findIndex((val) => { return val === id; });
        if (index >= 0) {
            batch.splice(index, 1);
        } else {
            batch.push(id);
        }
        await this.setState({...this.state, selectedUserIdsForBatch: batch});
    }
    private async reportTypeDropdownChanged(e: any, userId: number) {
        e.preventDefault();
        let fullReportUsers = this.state.selectedUserIdsForLimitedReport;
        const index = fullReportUsers.findIndex((val) => { return val === userId; });
        if (index >= 0) {
            fullReportUsers.splice(index, 1);
        } else {
            fullReportUsers.push(userId);
        }
        await this.setState({...this.state, selectedUserIdsForLimitedReport: fullReportUsers});
    }
    private async toggleAllForExportReportType(e: any) {
        e.preventDefault();
        const selectedCount = this.state.selectedUserIdsForLimitedReport.length;
        const userCount = this.state.selectedUserIdsForBatch.length;
        if (selectedCount !== userCount) {
            let allIds = [];
            for(let id of this.state.selectedUserIdsForBatch) allIds.push(id);
            this.setState({selectedUserIdsForLimitedReport: allIds});
        }
        else {
            this.setState({selectedUserIdsForLimitedReport: []});
        }
    }
    private async toggleAllForPersonalityTypes(e: any) {
        e.preventDefault();
        const selectedCount = this.state.selectedUserIdsForTypesReport.length;
        const userCount = this.state.selectedUserIdsForBatch.length;
        if (selectedCount !== userCount) {
            let allIds = [];
            for(let id of this.state.selectedUserIdsForBatch) allIds.push(id);
            this.setState({selectedUserIdsForTypesReport: allIds});
        }
        else {
            this.setState({selectedUserIdsForTypesReport: []});
        }
    }
    private async handleToggleTypesReport(id: number) {
        let batch = this.state.selectedUserIdsForTypesReport;
        const index = batch.findIndex((val) => { return val === id; });
        if (index >= 0) {
            batch.splice(index, 1);
        } else {
            batch.push(id);
        }
        await this.setState({...this.state, selectedUserIdsForTypesReport: batch});
    }
    private async toggleAllForBatch(e: any) {
        e.preventDefault();
        let confirmed = this.state.confirmedTeamMembers.filter(
            (m) => { return m !== null && m !== undefined; });
        let batch = this.state.selectedUserIdsForBatch;
        if (batch.length === confirmed.length) {
            batch.splice(0);
        } else {
            batch = confirmed.map((m) => { return m.userID || -1; });
        }
        await this.setState({...this.state, selectedUserIdsForBatch: batch});
    }
    createLoginsForBatch = async (e: any) => {
        e.preventDefault();
        this.setState({isLoading: true});
        for (const userId of this.state.selectedUserIdsForBatch) {
            try {
                await this.userDataService.createPersonalAccount(userId);
                this.props.notificationService.addNotification(`Created login for ${this.getUserName(userId)}`);
            } catch (e) {
                console.log(`Exception while creating logins for batch: `);
                console.log(e);
                this.props.notificationService.addError(`Failed to create login for ${this.getUserName(userId)}`);
            }
        }
        this.setState({isLoading: false});
    }
    inviteBatch = async (e: any) => {
        e.preventDefault();
        this.setState({isLoading: true});
        for (const userId of this.state.selectedUserIdsForBatch) {
            try {
                this.state.inviteSentByUserId[userId] = undefined;
                this.forceUpdate();
                let expirationDate = this.state.inviteExpiration.toDate();
                let values = {
                    userID: userId,
                    expirationDate: expirationDate.toUTCString(),
                    surveyVersionID: this.props.FilterStore!.surveyVersion!.id,
                };
                this.surveyInstanceDataService.create(values as any).then(() => {
                    this.state.inviteSentByUserId[userId] = true;
                    this.forceUpdate();
                });
            } catch (e) {
                console.log(`Exception while creating batch survey invites: `);
                console.log(e);
            }
        }
        this.setState({isLoading: false});
    }
    openBatchModal = (e: any) => {
        e.preventDefault();
        this.setState({...this.state, selectedUserIdsForTypesReport:[], selectedUserIdsForLimitedReport:[], showModal: true});
    }
    exportBatch = async (e: any) => {
        e.preventDefault();
        this.props.notificationService.addNotification(`Individual report email batch started`);
        this.setState({isLoading:true});

        let promises = [];

        for(const userId of this.state.selectedUserIdsForLimitedReport) {
            const latestSurveyInstance = await this.getLatestCompletedInstanceForUserId(userId);
            if (latestSurveyInstance === null) continue;
            const includeTypes = this.state.selectedUserIdsForTypesReport.findIndex(uid => uid === userId) >= 0;
            promises.push(this.reportDataService.createEmailRequestForLimitedIndividualReport(
                latestSurveyInstance.id, this.surveyReportType!.id, includeTypes
            ));
        }
        for(const userId of this.state.selectedUserIdsForBatch) {
            if (this.state.selectedUserIdsForLimitedReport.findIndex(uid => uid === userId) < 0) {
                const latestSurveyInstance = await this.getLatestCompletedInstanceForUserId(userId);
                if (latestSurveyInstance === null) continue;
                const includeTypes = this.state.selectedUserIdsForTypesReport.findIndex(uid => uid === userId) >= 0;
                promises.push(this.reportDataService.createEmailRequestForFullIndividualReport(
                    latestSurveyInstance.id, this.surveyReportType!.id, includeTypes
                ));
            }
        }
        await Promise.all(promises);

        this.props.notificationService.addNotification(`Individual report email batch complete`);
        this.setState({isLoading:false});

        this.hideBatchModal();
    }
    getUserName = (userId: number) => {
        for (const teamuser of this.state.confirmedTeamMembers) {
            if (teamuser.userID === userId && teamuser.user) {
                return `${teamuser.user.lastName}, ${teamuser.user.firstName}`;
            }
        }
        return 'Nameless';
    }
    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }
    getUserTeamForUserId(userId: number) {
        const index = this.state.confirmedTeamMembers.findIndex((val) => { return val.userID === userId});
        if (index < 0) {
            return null;
        }
        return this.state.confirmedTeamMembers[index];
    }
    userIsAssignedLimitedReport = (userId: number) => {
        const index = this.state.selectedUserIdsForLimitedReport.findIndex((val) => { return val === userId; })
        return index >= 0;
    }
    InstanceCollection = (instances:any) => {
        return {
            instances,
            sortByAge: () => {
                instances.sort((a:any,b:any) => {
                    const aDate = new Date(a.modifiedDate);
                    const bDate = new Date(b.modifiedDate);
                    return aDate === bDate ? 0 : aDate > bDate ? -1 : 1;
                })
                return this.InstanceCollection(instances);
            },
            sortByActive: () => {
                instances.sort((a:any,b:any) => {
                    return a.active === b.active ? 0 : a.active ? -1 : 1;
                });
                return this.InstanceCollection(instances);
            },
            dropDeleted: () => this.InstanceCollection(instances.filter((i:any) => {
                return !i.isDeleted;
            })),
            dropIncomplete: () => this.InstanceCollection(instances.filter((i:any) => {
                return !!i.completedDate;
            })),
        };
    }

    getSurveyInstancesForUserId = async (userId: number) => {
        return this.InstanceCollection(await this.surveyInstanceDataService.getAllForUser(userId));
    }
    userHasInvite = async (id: number) => {
        const collection = (await this.getSurveyInstancesForUserId(id))
            .dropDeleted();
        return collection.instances.length > 0;
    }
    getLatestCompletedInstanceForUserId = async (userId: number) => {
        let instances = await this.getSurveyInstancesForUserId(userId);
        const collection = (instances)
            .dropDeleted().dropIncomplete().sortByAge().sortByActive();
        if (collection.instances.length > 0) {
            return collection.instances[0];
        }
        return null;
    }

    private handleIsMemberChange(userId: number) {
        const { confirmedTeamMembers } = this.state;

        const teamMembers = confirmedTeamMembers.map((x: UserTeam) => {
            if (x.userID !== userId) {
                return x;
            }

            x.isMember = !x.isMember;
            return x;
        });

        this.setState({ confirmedTeamMembers: teamMembers, saveNeeded: true });
    }

    private handleIsMemberChangeBinder(id: number): any {
        return this.handleIsMemberChange.bind(this, id);
    }

    private handleFormSubmit(values: any) {
        this.setState({saveNeeded: false});
        values.members = this.state.confirmedTeamMembers;
        values.surveyCompleteUsers = this.state.confirmedAssociateTeamMembers;

        this.props.submit(values);
    }

    // Validation methods
    validateErrors(values: FormValues) {
        const { name, organizationId } = values;
        let formErrors: any = {};

        if (name || name === '') {
            if (!this.vs.required(name)) {
                formErrors.name = 'Name is required.';
            } else if (!this.vs.cannotBeEmpty(name!)) {
                formErrors.name = 'Name cannot be blank.';
            }
        }

        if (organizationId) {
            if (!this.vs.required(organizationId)) {
                formErrors.organizationId = 'Organization is required.';
            }
        }

        return formErrors;
    }

    validateSuccess(values: FormValues, errors: FormErrors) {
        // make sure we have no errors and all the required values
        if (R.isEmpty(errors) && this.vs.validateRequiredFieldsExist(['name'], values)) {
            this.setState({ canSubmit: true, formErrors: errors });
        } else {
            this.setState({ canSubmit: false, formErrors: errors });
        }

        return errors;
    }
    // end of validation stuff

    private userTeamFilter(user1: UserTeam, user2: UserTeam) {
        if (user1.user && user2.user) {
            if (user1.user.lastName > user2.user.lastName) {
                return 1;
            } else if (user1.user.lastName < user2.user.lastName) {
                return -1;
            }
            if (user1.user.firstName > user2.user.firstName) {
                return 1;
            } else if (user1.user.firstName < user2.user.firstName) {
                return -1;
            }
        }
        return 0;
    }

    handleDatepickerOnChange = (newDate: moment.Moment) => {
        this.setState({ inviteExpiration: newDate });
    }

    hideBatchModal = () => {
        this.setState({showModal: false});
    };

    public render() {
        const { selectedOrganizationId, teamMemberTypeahead, teamAssociateMemberTypeahead, confirmedTeamMembers, confirmedAssociateTeamMembers, formErrors } = this.state;
        const organizationOptions = !this.props.organizations ? [] : this.props.organizations.map((organization) => {
            return {
                label: organization.name,
                value: organization.id
            };
        });
        const style = { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)" };

        return (
            <Form
                defaultValues={this.props.defaultValues}
                onSubmit={submittedValues => this.handleFormSubmit(submittedValues)}
                formDidUpdate={this.formDidUpdate}
                validateError={this.validateErrors}
                validateSuccess={this.validateSuccess}
            >
                {formApi => (
                    <form onSubmit={formApi.submitForm} id="entityForm">
                        <Prompt
                            when={this.state.saveNeeded}
                            message='There are unsaved changes. Are you sure you want to leave?'
                        />
                        {this.state.isLoading && <LoadingIndicator></LoadingIndicator>}
                        <div className="form-group">
                            <label htmlFor="name">Name *</label>
                            <Text className="form-control" field="name" id="name"/>
                            {formErrors.name && <span className="help-block error-text">{formErrors.name}</span>}
                        </div>
                        {['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) &&
                            <div className="form-group">
                                <label htmlFor="organizationId">Organization *</label>
                                <Select className="form-control" field="organizationId" id="organizationId"
                                        options={organizationOptions}/>
                                {formErrors.organizationId &&
                                    <span className="help-block error-text">{formErrors.organizationId}</span>}
                            </div>}
                        {selectedOrganizationId &&
                            <div>
                                <div className="form-group">
                                    <label>Team Members</label>
                                    <InputGroup>
                                        <AsyncTypeahead
                                            ref={(typeahead: any) => this.teamMemberTypeahead = typeahead}
                                            {...teamMemberTypeahead}
                                            minLength={2}
                                            onSearch={this.handleSearch}
                                            onChange={this.typeaheadOnChange}
                                            placeholder="Search for a user"
                                            labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                        />
                                        <InputGroup.Button>
                                            <button onClick={this.addUser} className="btn btn-default">Add</button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                                {(confirmedTeamMembers && confirmedTeamMembers.length > 0) &&
                                    <table className="table table-striped">
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th>Is Member?</th>
                                                <th>Is Manager?</th>
                                                {this.canBatch && <th>Invite sent?</th>}
                                                {this.canBatch && <th>Survey completed?</th>}
                                                {this.canBatch && <th>Include in Batch</th>}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {confirmedTeamMembers.sort(this.userTeamFilter).map((userteam, index) => {
                                            return (
                                                <tr key={`user-${userteam.userID}`}>
                                                    <td>
                                                        {userteam.user!.lastName}, {userteam.user!.firstName}
                                                    </td>
                                                    <td>
                                                        <input type="checkbox" checked={userteam.isMember}
                                                               onChange={this.handleIsMemberChangeBinder(userteam.user!.id)}/>
                                                    </td>
                                                    <td>
                                                        <input type="checkbox" checked={userteam.isManager}
                                                               onChange={this.handleIsManagerChangeBinder(userteam.user!.id)}/>
                                                    </td>
                                                    {this.canBatch && <td>
                                                        {(this.state.inviteSentByUserId[userteam.userID!] === undefined || this.state.inviteSentByUserId[userteam.userID!] === null) &&
                                                            <span
                                                                className="glyphicon glyphicon-refresh glyphicon-spin"></span>} {this.state.inviteSentByUserId[userteam.userID!] &&
                                                        <span className="glyphicon glyphicon-ok"></span>}
                                                    </td>}
                                                    {this.canBatch && <td>
                                                        {(this.state.surveyCompletedByUserId[userteam.userID!] === undefined || this.state.surveyCompletedByUserId[userteam.userID!] === null) &&
                                                            <span
                                                                className="glyphicon glyphicon-refresh glyphicon-spin"></span>} {this.state.surveyCompletedByUserId[userteam.userID!] === false &&
                                                        <span></span>} {this.state.surveyCompletedByUserId[userteam.userID!] &&
                                                        <span>{(new Date(this.state.surveyCompletedByUserId[userteam.userID!].completedDate)).toLocaleDateString('en-US')}</span>}
                                                    </td>}
                                                    {this.canBatch && <td>
                                                        <input type="checkbox"
                                                               checked={this.state.selectedUserIdsForBatch.includes(userteam.user!.id)}
                                                               onChange={() => this.handleSelectUser(userteam.user!.id)}/>
                                                    </td>}
                                                    <td>
                                                        <span onClick={this.removeUserBinder(userteam.user!.id)}
                                                              className="glyphicon glyphicon-trash"/>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {this.canBatch &&
                                            <tr className="batch-control-container">
                                                <td className="batch-control-title">
                                                    Batch actions
                                                </td>
                                                <td/>
                                                <td/>
                                                <td>
                                                    <span>Expiration</span>
                                                    <div className="invite-expiration-picker">
                                                        <Datepicker
                                                            popperPlacement={"top"}
                                                            selected={this.state.inviteExpiration}
                                                            className="invite-expiration-picker"
                                                            onChange={this.handleDatepickerOnChange}
                                                            dropdownMode="select"
                                                            maxDate={moment().add(1, 'y')}
                                                            minDate={moment()}
                                                        />
                                                    </div>
                                                </td>
                                                <td/>
                                                <td>
                                                    <button className="btn batch-button" onClick={(e) => {
                                                        this.toggleAllForBatch(e);
                                                    }}>Include all
                                                    </button>
                                                </td>
                                                <td/>
                                            </tr>
                                        }
                                        {this.canBatch &&
                                            <tr className="batch-control-container">
                                                <td/>
                                                <td/>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.inviteBatch(e)}
                                                    >Invite batch
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.createLoginsForBatch(e)}
                                                    >Create logins
                                                    </button>
                                                </td>
                                                <td>
                                                    <button
                                                        className="btn btn-primary batch-button"
                                                        disabled={this.state.isLoading}
                                                        onClick={(e) => this.openBatchModal(e)}
                                                    >PDF batch
                                                    </button>
                                                </td>
                                                <td/>
                                                <td/>
                                            </tr>
                                        }
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }

                        {selectedOrganizationId && ['superAdmin', 'siteAdmin', 'ponderaAssociate'].includes(this.userRole) &&
                            <div>
                                <div className="form-group">
                                    <label>Associate Members</label>
                                    <InputGroup>
                                        <AsyncTypeahead
                                            ref={(typeahead: any) => this.teamAssociateMemberTypeahead = typeahead}
                                            {...teamAssociateMemberTypeahead}
                                            minLength={2}
                                            onSearch={this.handleAssociateSearch}
                                            onChange={this.typeaheadAssociateOnChange}
                                            placeholder="Search for a user"
                                            labelKey={(option) => `${option.lastName}, ${option.firstName}`}
                                        />
                                        <InputGroup.Button>
                                            <button onClick={this.addAssociateUser} className="btn btn-default">Add
                                            </button>
                                        </InputGroup.Button>
                                    </InputGroup>
                                </div>
                                {(confirmedAssociateTeamMembers && confirmedAssociateTeamMembers.length > 0) &&
                                    <table className="table table-striped">
                                        <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th/>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {confirmedAssociateTeamMembers.map((userteam, index) => (
                                            <tr key={`user-${userteam.userID}`}>
                                                <td>
                                                    {userteam.user!.lastName}, {userteam.user!.firstName}
                                                </td>
                                                <td>
                                                    <span onClick={this.removeAssociateUserBinder(userteam.user!.id)}
                                                          className="glyphicon glyphicon-trash"/>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                }
                            </div>
                        }

                        <button type="submit" className="btn btn-primary" disabled={!this.state.canSubmit}>Save</button>
                        { this.state.showModal && <div className="modal-container">
                            <div className="row">
                                <div className="col-xs-offset-1 col-xs-10 col-sm-offset-2 col-sm-8">
                                    <div className="modal-dialog">
                                        <div className="modal-content" role="document">
                                            <div id='modal-thing'
                                                 className="modal-header pva-group-title pondera-modal-header">
                                                <button type="button" className="close pondera-close"
                                                        onClick={(e) => this.hideBatchModal()}>
                                                    <span className="glyphicon glyphicon-remove"/>
                                                    <span className="sr-only">Close</span>
                                                </button>
                                                <div className="modal-title">
                                                    <div className="pondera-modal-title batch-modal-title">Batch export</div>
                                                </div>
                                            </div>
                                            <div className="modal-body">
                                                {this.state.isLoading && <div className={"centered-spinner"}><LoadingIndicator></LoadingIndicator></div>}
                                                <table className="table table-striped">
                                                    <thead>
                                                    <tr>
                                                        <th>Name</th>
                                                        {<th>Report type</th>}
                                                        {<th>Include types?</th>}
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {this.state.selectedUserIdsForBatch.map((userId) => {
                                                        const typeDropdownId = `reportTypeFor_${userId}`;
                                                        const fullOption = {label:'Full',value:'full'};
                                                        const userteam = this.getUserTeamForUserId(userId);
                                                        if (userteam === null) {
                                                            return;
                                                        }
                                                        const limitedOption = {label:'Limited',value:'limited'};
                                                            return (
                                                                <tr key={`user-${userId}`}>
                                                                    <td>
                                                                        {userteam.user!.lastName}, {userteam.user!.firstName}
                                                                    </td>
                                                                    <td>
                                                                        <div className="form-group">
                                                                            <select className="form-control" id={typeDropdownId} onChange={(e) => {this.reportTypeDropdownChanged(e, userteam.userID!)}}>
                                                                                <option key={userteam.userID + '_0'} value={fullOption.value} selected={!this.userIsAssignedLimitedReport(userteam.userID!)}>{fullOption.label}</option>
                                                                                <option key={userteam.userID + '_1'} value={limitedOption.value} selected={this.userIsAssignedLimitedReport(userteam.userID!)}>{limitedOption.label}</option>
                                                                            </select>
                                                                        </div>
                                                                    </td>
                                                                    <td>
                                                                        <input type="checkbox"
                                                                               checked={this.state.selectedUserIdsForTypesReport.includes(userteam.user!.id)}
                                                                               onChange={() => this.handleToggleTypesReport(userteam.user!.id)}/>
                                                                    </td>
                                                                </tr>
                                                        );
                                                        }
                                                    )}
                                                        <tr>
                                                            <td></td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-primary batch-button"
                                                                    disabled={this.state.isLoading}
                                                                    onClick={(e) => this.toggleAllForExportReportType(e)}
                                                                >Switch report type
                                                                </button>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-primary batch-button"
                                                                    disabled={this.state.isLoading}
                                                                    onClick={(e) => this.toggleAllForPersonalityTypes(e)}
                                                                >Toggle include types
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div className="modal-footer">
                                                <button onClick={(e) => { this.exportBatch(e); }} type="button"
                                                        className="btn btn-primary modal-send"
                                                        disabled={this.state.isLoading}>Send
                                                </button>
                                                <button onClick={(e) => this.hideBatchModal()} type="button"
                                                        className="btn btn-default">Cancel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>}
                    </form>
                )}
            </Form>
        );
    }
    }


// WEBPACK FOOTER //
// ./src/components/team/TeamForm.tsx